import { useCallback, useState } from "react";
import type { V10ClientLinkViewProps } from "../types.js";
import { v10LinkIdFromContext } from "../types.js";

function V10ClientPreviewRouteTabs({
  routes,
  activeRoute,
  onRouteChange,
  loading,
}: {
  readonly routes: readonly string[];
  readonly activeRoute: string;
  readonly onRouteChange: (route: string) => void;
  readonly loading: boolean;
}) {
  if (routes.length <= 1) return null;
  return (
    <div className="v10-cl-preview-route-tabs" role="tablist" aria-label="Preview pages">
      {routes.map((route) => (
        <button
          key={route}
          type="button"
          role="tab"
          className={`v10-cl-preview-route-tab${route === activeRoute ? " active" : ""}`}
          aria-selected={route === activeRoute}
          disabled={loading}
          onClick={() => onRouteChange(route)}
        >
          {route === "/" ? "Home" : route.replace(/^\//, "").replace(/-/g, " ")}
        </button>
      ))}
    </div>
  );
}

export function V10PreviewTab({
  shell,
  preview,
  active,
  api,
  locationContext,
  sessionToken,
  approvalBaselineVersionId,
  onPreviewChange,
}: Pick<V10ClientLinkViewProps, "shell" | "preview" | "api" | "locationContext" | "sessionToken"> & {
  readonly active: boolean;
  readonly approvalBaselineVersionId?: string | null;
  readonly onPreviewChange?: (preview: NonNullable<V10ClientLinkViewProps["preview"]>) => void;
}) {
  const displayName = preview?.displayName ?? shell.project.displayName;
  const routes = preview?.availableRoutes ?? (preview?.previewRoute ? [preview.previewRoute] : ["/"]);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const linkId = v10LinkIdFromContext(locationContext);
  const previewStale =
    approvalBaselineVersionId != null &&
    preview?.previewVersionId != null &&
    preview.previewVersionId !== approvalBaselineVersionId;

  const handleRouteChange = useCallback(
    async (route: string) => {
      if (!preview || route === preview.previewRoute) return;
      setLoadingRoute(true);
      try {
        const next = await api.getPreview(linkId, sessionToken, route);
        onPreviewChange?.(next);
      } finally {
        setLoadingRoute(false);
      }
    },
    [api, linkId, onPreviewChange, preview, sessionToken],
  );

  return (
    <section
      className={`v10-cl-tab-panel${active ? " active" : ""}`}
      role="tabpanel"
      id="v10-tab-preview"
      aria-labelledby="v10-tab-btn-preview"
      hidden={!active}
    >
      {preview?.previewVersionId ? (
        <p className="v10-cl-preview-version" data-testid="preview-version-id">
          Preview version <code>{preview.previewVersionId}</code>
        </p>
      ) : null}
      {previewStale ? (
        <p className="v10-cl-preview-stale" role="alert" data-testid="preview-stale-banner">
          This preview changed since you opened review. Refresh routes or return to Summary before approving.
        </p>
      ) : null}
      <V10ClientPreviewRouteTabs
        routes={routes}
        activeRoute={preview?.previewRoute ?? "/"}
        onRouteChange={(route) => void handleRouteChange(route)}
        loading={loadingRoute}
      />
      {preview?.previewHtml ? (
        <div className="v10-cl-preview-frame" aria-label="Website preview">
          <div
            className="v10-cl-preview-html preview-frame"
            dangerouslySetInnerHTML={{ __html: preview.previewHtml }}
          />
        </div>
      ) : (
        <div className="v10-cl-preview-box" aria-label="Website preview">
          <div className="v10-cl-preview-browser">
            <span />
            <span />
            <span />
          </div>
          <div className="v10-cl-fake-site">
            <h3>{displayName}</h3>
            <p className="v10-cl-muted">
              Preview snapshot appears here when ready. No editing happens directly from preview.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
